import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import type { UserRoom, RoomSnapshot } from "@tuit/shared-types";
import {
  updateUserRoom,
  addSourceToRoom,
  removeSourceFromRoom,
  reorderRoomSources,
  invalidateSnapshot,
} from "../api.js";
import { SourceSearch } from "../components/SourceSearch.js";
import { SectionView } from "../components/SectionView.js";
import { getFaviconUrl } from "../utils/favicon.js";
import styles from "../styles/pages/RoomEditorPage.module.css";

interface RoomSource {
  sourceId: string;
  title: string;
  position: number;
}

const EMPTY_SET = new Set<string>();
const EMPTY_MAP = new Map<string, string[]>();
const noop = () => {};
const noopRef = () => {};

export function RoomEditorPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const params = useParams({ strict: false });
  const roomId = params.roomId as string;
  const navigate = useNavigate();

  const [room, setRoom] = useState<UserRoom | null>(null);
  const [sources, setSources] = useState<RoomSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [roomSnapshot, setRoomSnapshot] = useState<RoomSnapshot | null>(null);
  const [dragSrcId, setDragSrcId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  // Share tab
  const appUrl = window.location.origin;
  const layoutRef = useRef<HTMLDivElement>(null);
  const sidebarWidthRef = useRef(380);

  useEffect(() => {
    if (isLoaded && !isSignedIn) navigate({ to: "/" });
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!isSignedIn || !roomId) return;
    Promise.all([
      fetch(`/api/user-room/${roomId}`).then((r) => r.json()) as Promise<UserRoom>,
      fetch(`/api/user-room/${roomId}/sources`).then((r) => r.json()) as Promise<{ sources: RoomSource[] }>,
      fetch(`/api/user-room/${roomId}/snapshot`).then((r) => r.json()) as Promise<RoomSnapshot>,
    ]).then(([roomData, sourcesData, snapshotData]) => {
      setRoom(roomData);
      setTitle(roomData.title);
      setSlug(roomData.slug);
      setIsPublic(roomData.isPublic);
      setSources(sourcesData.sources);
      setRoomSnapshot(snapshotData);
    }).finally(() => setLoading(false));
  }, [isSignedIn, roomId]);

  function handleReorder(srcId: string, dropId: string) {
    setSources((prev) => {
      const next = [...prev];
      const ai = next.findIndex((s) => s.sourceId === srcId);
      const bi = next.findIndex((s) => s.sourceId === dropId);
      if (ai === -1 || bi === -1) return prev;
      const [item] = next.splice(ai, 1);
      next.splice(bi, 0, item!);
      const updated = next.map((s, i) => ({ ...s, position: i }));
      reorderRoomSources(roomId, updated.map((s) => s.sourceId)).catch(() => {});
      invalidateSnapshot(roomId);
      return updated;
    });
  }

  function refreshSnapshot() {
    fetch(`/api/user-room/${roomId}/snapshot`)
      .then((r) => r.json())
      .then((data) => setRoomSnapshot(data as RoomSnapshot))
      .catch(() => {});
  }

  async function handleSaveSettings(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const updated = await updateUserRoom(roomId, { title, slug, isPublic });
      setRoom(updated);
      invalidateSnapshot(roomId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddSource(source: { id: string; title: string; position?: number }) {
    await addSourceToRoom(roomId, source.id);
    setSources((prev) => [...prev, { sourceId: source.id, title: source.title, position: prev.length }]);
    invalidateSnapshot(roomId);
    refreshSnapshot();
  }

  async function handleRemoveSource(sourceId: string) {
    setRemovingId(sourceId);
    try {
      await removeSourceFromRoom(roomId, sourceId);
      setSources((prev) => prev.filter((s) => s.sourceId !== sourceId));
      invalidateSnapshot(roomId);
      refreshSnapshot();
    } finally {
      setRemovingId(null);
    }
  }

  useEffect(() => {
    layoutRef.current?.style.setProperty("--editor-sidebar-width", `${sidebarWidthRef.current}px`);
  }, []);

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    const startX = e.clientX;
    const startWidth = sidebarWidthRef.current;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function onMove(ev: MouseEvent) {
      const next = Math.max(240, Math.min(640, startWidth + ev.clientX - startX));
      sidebarWidthRef.current = next;
      layoutRef.current?.style.setProperty("--editor-sidebar-width", `${next}px`);
    }

    function onUp() {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const previewSections = useMemo(() => {
    if (!roomSnapshot) return [];
    const orderMap = new Map(sources.map((s, i) => [s.sourceId, i]));
    return roomSnapshot.sourceSnapshots
      .filter((ss) => orderMap.has(ss.sourceId))
      .sort((a, b) => (orderMap.get(a.sourceId) ?? 0) - (orderMap.get(b.sourceId) ?? 0));
  }, [roomSnapshot, sources]);

  const sourceDomains = useMemo(() => {
    const map = new Map<string, string>();
    for (const ss of roomSnapshot?.sourceSnapshots ?? []) {
      const domain = ss.items[0]?.domain;
      if (domain) map.set(ss.sourceId, domain);
    }
    return map;
  }, [roomSnapshot]);

  if (!isLoaded || !isSignedIn) return null;
  if (loading) return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={`${styles.skelBtn} ${styles.skel}`} />
        <div className={`${styles.skelTitle} ${styles.skel}`} />
        <div className={`${styles.skelBtn} ${styles.skel}`} />
      </header>
      <div className={styles.loadingBody}>
        <div className={styles.loadingSpinner} />
      </div>
    </div>
  );
  if (!room) return <div className={styles.page}><p className={styles.hint}>Room not found.</p></div>;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/my-rooms" className={styles.back}>← My Rooms</Link>
        <h1 className={styles.title}>{room.title}</h1>
        <Link to="/$roomSlug" params={{ roomSlug: `~${room.id}` }} className={styles.viewBtn}>
          View Room →
        </Link>
      </header>

      <div className={styles.layout} ref={layoutRef}>
        <div className={styles.editorSidebar}>
          <div className={styles.body}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Settings</h2>
              <form className={styles.form} onSubmit={handleSaveSettings}>
                <label className={styles.label}>
                  Title
                  <input
                    type="text"
                    className={styles.input}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </label>
                <label className={styles.label}>
                  Slug
                  <input
                    type="text"
                    className={styles.input}
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    required
                  />
                </label>
                <label className={styles.checkLabel}>
                  <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                  Public
                </label>
                {error && <p className={styles.error}>{error}</p>}
                <div className={styles.formActions}>
                  <button type="submit" className={styles.saveBtn} disabled={saving}>
                    {saving ? "Saving…" : "Save Settings"}
                  </button>
                </div>
              </form>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Sources ({sources.length})</h2>
              <ul className={styles.sourceList}>
                {sources.map((s) => {
                  const isDragging = dragSrcId === s.sourceId;
                  const isOver = dragOverId === s.sourceId && !isDragging;
                  return (
                    <li
                      key={s.sourceId}
                      className={`${styles.sourceItem} ${isDragging ? styles.sourceItemDragging : ""} ${isOver ? styles.sourceItemDragOver : ""}`}
                      draggable
                      onDragStart={(e) => {
                        setDragSrcId(s.sourceId);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => { setDragSrcId(null); setDragOverId(null); }}
                      onDragEnter={() => setDragOverId(s.sourceId)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragSrcId && dragSrcId !== s.sourceId) handleReorder(dragSrcId, s.sourceId);
                        setDragSrcId(null);
                        setDragOverId(null);
                      }}
                    >
                      <span className={styles.dragHandle}>⠿</span>
                      {sourceDomains.get(s.sourceId) && (
                        <img
                          src={getFaviconUrl(sourceDomains.get(s.sourceId)!)}
                          width={14}
                          height={14}
                          alt=""
                          aria-hidden="true"
                          className={styles.sourceFavicon}
                        />
                      )}
                      <span className={styles.sourceName}>{s.title}</span>
                      <button
                        type="button"
                        className={styles.removeBtn}
                        onClick={() => handleRemoveSource(s.sourceId)}
                        disabled={removingId === s.sourceId}
                      >
                        {removingId === s.sourceId ? "…" : "Remove"}
                      </button>
                    </li>
                  );
                })}
                {sources.length === 0 && (
                  <li className={styles.hint}>No sources yet — add some below.</li>
                )}
              </ul>
            </section>

            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Add Sources</h2>
              <SourceSearch
                excludeIds={sources.map((s) => s.sourceId)}
                onAdd={handleAddSource}
              />
            </section>

            {room.isPublic && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Share</h2>
                <div className={styles.shareRow}>
                  <span className={styles.shareLabel}>Public URL</span>
                  <div className={styles.shareInput}>
                    <span className={styles.shareValue}>{appUrl}/~{room.slug}</span>
                    <button type="button" className={styles.copyBtn} onClick={() => navigator.clipboard.writeText(`${appUrl}/~${room.slug}`)}>Copy</button>
                  </div>
                </div>
                <div className={styles.shareRow}>
                  <span className={styles.shareLabel}>RSS Feed</span>
                  <div className={styles.shareInput}>
                    <span className={styles.shareValue}>/api/rooms/{room.slug}/feed.xml</span>
                    <button type="button" className={styles.copyBtn} onClick={() => navigator.clipboard.writeText(`${appUrl}/api/rooms/${room.slug}/feed.xml`)}>Copy</button>
                  </div>
                </div>
                <div className={styles.shareRow}>
                  <span className={styles.shareLabel}>Embed</span>
                  <div className={styles.shareInput}>
                    <span className={styles.shareValue} style={{ fontSize: 11 }}>{`<iframe src="${appUrl}/api/rooms/${room.slug}/widget" />`}</span>
                    <button type="button" className={styles.copyBtn} onClick={() => navigator.clipboard.writeText(`<iframe src="${appUrl}/api/rooms/${room.slug}/widget" width="400" height="500" frameborder="0"></iframe>`)}>Copy</button>
                  </div>
                </div>

              </section>
            )}
          </div>
        </div>

        <div
          className={styles.divider}
          onMouseDown={handleDividerMouseDown}
          role="separator"
          aria-orientation="vertical"
        />

        <div className={styles.preview}>
          {previewSections.length === 0 ? (
            <p className={styles.previewEmpty}>
              {sources.length === 0 ? "Add sources to see a preview." : "Loading preview…"}
            </p>
          ) : (
            previewSections.map((section) => (
              <SectionView
                key={section.sourceId}
                section={section}
                readIds={EMPTY_SET}
                onRead={noop}
                suppressed={EMPTY_SET}
                alsoIn={EMPTY_MAP}
                sectionRef={noopRef}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
